import { useEffect } from "react";
import type { BlogPost } from "@/lib/blog";

interface Props {
  post: BlogPost;
  url: string;
}

/**
 * Injects JSON-LD Article (+ optional FAQPage) into <head>.
 * Removes its own scripts on unmount to prevent duplication.
 */
const ArticleSchema = ({ post, url }: Props) => {
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];

    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.seoDescription || post.excerpt,
      image: post.coverImage
        ? [`https://leadership-focus.de${post.coverImage}`]
        : undefined,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Person",
        name: post.author,
      },
      publisher: {
        "@type": "Organization",
        name: "Leadership Focus",
        logo: {
          "@type": "ImageObject",
          url: "https://leadership-focus.de/favicon.svg",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url,
      },
      articleSection: post.category,
      keywords: Array.from(
        new Set([...(post.keywords ?? []), ...(post.tags ?? [])]),
      ).join(", ") || undefined,
    };

    const articleScript = document.createElement("script");
    articleScript.type = "application/ld+json";
    articleScript.text = JSON.stringify(article);
    articleScript.dataset.lfSchema = "article";
    document.head.appendChild(articleScript);
    scripts.push(articleScript);

    if (post.faq && post.faq.length > 0) {
      const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
          },
        })),
      };
      const faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.text = JSON.stringify(faqLd);
      faqScript.dataset.lfSchema = "faq";
      document.head.appendChild(faqScript);
      scripts.push(faqScript);
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [post, url]);

  return null;
};

export default ArticleSchema;
